import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Handshake,
  Clock,
  Wallet,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  PlayCircle,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  MessageCircle,
  Zap,
  TrendingUp,
  BadgeCheck,
  Receipt,
  FileText,
  Rocket,
  Phone,
  ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */
const WHATSAPP_NUMBER = "5511956649968";
const FDP_MESSAGE =
  "Olá AMC Escolas! Quero contratar meu primeiro orçamento e garantir minha escola no sistema Faz Depois Paga.";
const whatsappLink = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

/* ------------------------------------------------------------------ */
/* Reusable CTA — "Quero contratar meu primeiro orçamento"             */
/* ------------------------------------------------------------------ */
const QueroContratarCTA = ({ size = "lg", variant = "solid", testid }) => {
  const base =
    "group inline-flex items-center justify-center gap-2 font-black rounded-2xl transition-all active:scale-[0.98] shadow-xl";
  const sizes = {
    lg: "text-[13px] sm:text-sm h-14 px-5 sm:px-7",
    md: "text-[12.5px] h-12 px-5",
  };
  const variants = {
    solid:
      "bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-600 text-white shadow-emerald-500/40",
    outline:
      "bg-white hover:bg-emerald-50 text-emerald-700 border-2 border-emerald-500 shadow-emerald-500/10",
  };
  return (
    <a
      href={whatsappLink(FDP_MESSAGE)}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className={`${base} ${sizes[size]} ${variants[variant]}`}
    >
      <Rocket className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.4} />
      <span className="text-left leading-tight">
        Quero contratar meu primeiro orçamento <br className="hidden sm:block" />
        <span className="font-bold opacity-90 text-[11px] sm:text-[12px]">
          e garantir minha escola no sistema
        </span>
      </span>
      <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.6} />
    </a>
  );
};

/* ------------------------------------------------------------------ */
/* Phone mockup — scattered through the page                           */
/* ------------------------------------------------------------------ */
const PhoneCard = ({ src, rotation = 0, delay = 0, scale = 1 }) => (
  <div
    style={{
      transform: `rotate(${rotation}deg) scale(${scale})`,
      animation: `fdpFloat 6s ease-in-out ${delay}s infinite`,
    }}
    className="relative w-[180px] sm:w-[220px] aspect-[9/19] rounded-[32px] bg-[#0b1536] p-1.5 shadow-2xl shadow-emerald-900/40"
  >
    <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-16 h-4 bg-black rounded-b-xl z-20" />
    <div className="relative w-full h-full rounded-[26px] bg-white overflow-hidden">
      {src ? (
        <img
          src={src}
          alt="Portal AMC"
          loading="lazy"
          className="w-full h-full object-cover object-top"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-b from-[#1e3a8a] to-[#4c1d95]" />
      )}
      <div className="pointer-events-none absolute inset-0 ring-1 ring-black/5 rounded-[26px]" />
    </div>
  </div>
);

const useMockups = () => {
  const [mockups, setMockups] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/landing/media?limit=20");
        const imgs = (r.data || []).filter((m) => m.kind === "image");
        setMockups(imgs.map((m) => `/api/files/${m.storage_path}`));
      } catch {
        /* ignore */
      }
    })();
  }, []);
  return mockups;
};

/* ------------------------------------------------------------------ */
/* Section wrapper                                                     */
/* ------------------------------------------------------------------ */
const Section = ({ id, className = "", children }) => (
  <section id={id} className={`relative px-5 sm:px-8 py-14 sm:py-20 ${className}`}>
    <div className="max-w-6xl mx-auto relative">{children}</div>
  </section>
);

const Kicker = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 text-[10px] font-black uppercase tracking-widest">
    <Sparkles className="w-3 h-3" />
    {children}
  </span>
);

/* ------------------------------------------------------------------ */
/* HERO                                                                */
/* ------------------------------------------------------------------ */
const Hero = ({ mockups }) => (
  <section className="relative overflow-hidden bg-[#061021] text-white">
    {/* Decorative glows */}
    <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-emerald-500/20 blur-[120px]" />
    <div className="absolute -bottom-40 right-0 w-[600px] h-[600px] rounded-full bg-emerald-400/10 blur-[140px]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.08)_1px,transparent_0)] bg-[length:28px_28px] opacity-50" />

    {/* Top bar */}
    <div className="relative z-20 flex items-center justify-between px-5 sm:px-8 py-4 max-w-6xl mx-auto">
      <Link to="/" data-testid="fdp-nav-back" className="inline-flex items-center gap-2">
        <Logo withIcon />
      </Link>
      <Link
        to="/"
        className="text-[12px] font-semibold text-white/70 hover:text-white inline-flex items-center gap-1"
        data-testid="fdp-nav-home"
      >
        <ArrowRight className="w-3.5 h-3.5 rotate-180" />
        Voltar à página inicial
      </Link>
    </div>

    <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 pb-16 sm:pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
      {/* Headline */}
      <div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-[10px] font-black uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Exclusivo AMC Escolas
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
          Faça a manutenção da sua escola{" "}
          <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-teal-300 bg-clip-text text-transparent">
            agora.
          </span>
          <br />
          <span className="text-white/80 font-black">Pague depois.</span>
        </h1>
        <p className="mt-5 text-[15px] sm:text-lg text-white/70 max-w-xl leading-relaxed">
          O sistema <strong className="text-emerald-300">Faz Depois Paga</strong>{" "}
          libera a sua escola para resolver pequenas manutenções no mesmo dia,
          acumular num único ciclo e pagar uma única nota ao final — com total
          controle, transparência e sem burocracia.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <QueroContratarCTA testid="hero-cta" />
          <a
            href="#como-funciona"
            data-testid="hero-see-how"
            className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold backdrop-blur transition-colors"
          >
            Ver como funciona <ChevronRight className="w-4 h-4" />
          </a>
        </div>

        {/* Mini trust strip */}
        <div className="mt-8 flex flex-wrap gap-5 text-[12px] text-white/70">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-300" /> Zero risco pra escola
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-emerald-300" /> Serviço imediato
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Receipt className="w-4 h-4 text-emerald-300" /> Uma nota ao final
          </span>
        </div>
      </div>

      {/* Phone cluster */}
      <div className="relative h-[380px] sm:h-[460px] flex items-center justify-center">
        <div className="absolute left-0 top-6 hidden sm:block">
          <PhoneCard src={mockups[0]} rotation={-10} delay={0} scale={0.85} />
        </div>
        <div className="relative z-10">
          <PhoneCard src={mockups[1] || mockups[0]} rotation={3} delay={0.6} />
        </div>
        <div className="absolute right-0 top-14 hidden sm:block">
          <PhoneCard src={mockups[2] || mockups[0]} rotation={12} delay={1.2} scale={0.85} />
        </div>
      </div>
    </div>

    <style>{`
      @keyframes fdpFloat { 0%,100% { translate: 0 0 } 50% { translate: 0 -10px } }
    `}</style>
  </section>
);

/* ------------------------------------------------------------------ */
/* Problem → Solution strip                                            */
/* ------------------------------------------------------------------ */
const BeforeAfter = () => (
  <Section className="bg-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>O que muda na prática</Kicker>
      <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-black text-[#0b1536]">
        O jeito antigo <span className="text-slate-400">vs.</span>{" "}
        <span className="text-emerald-600">Faz Depois Paga</span>
      </h2>
    </div>

    <div className="mt-10 grid md:grid-cols-2 gap-5">
      {/* Antes */}
      <div className="relative rounded-3xl bg-slate-50 border border-slate-200 p-6 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-slate-200/70" />
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
          Antes
        </p>
        <h3 className="mt-1 font-heading text-xl font-black text-slate-700">
          Burocracia e espera
        </h3>
        <ul className="mt-4 space-y-2.5 text-[13px] text-slate-600">
          {[
            "Pede orçamento, espera aprovação, perde tempo",
            "Precisa pagar antes para o serviço começar",
            "Uma nota pra cada reparo pequeno",
            "Difícil controlar o que já foi feito",
          ].map((t) => (
            <li key={t} className="flex gap-2">
              <span className="w-5 h-5 shrink-0 rounded-full bg-slate-200 text-slate-500 inline-flex items-center justify-center text-[12px]">
                ✕
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>

      {/* Depois */}
      <div className="relative rounded-3xl bg-gradient-to-br from-emerald-50 to-emerald-100/60 border-2 border-emerald-300 p-6 overflow-hidden shadow-xl shadow-emerald-500/10">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-emerald-200/80" />
        <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Com FDP
        </p>
        <h3 className="mt-1 font-heading text-xl font-black text-emerald-900">
          Imediato, acumulado, transparente
        </h3>
        <ul className="mt-4 space-y-2.5 text-[13px] text-emerald-900">
          {[
            "Chamou a AMC? Serviço começa no mesmo dia",
            "Zero risco: paga só depois do serviço concluído",
            "Uma nota ao final do ciclo — menos papelada",
            "Acompanhe cada atendimento pelo portal",
          ].map((t) => (
            <li key={t} className="flex gap-2">
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" strokeWidth={2.6} />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* How it works — 5 steps                                              */
/* ------------------------------------------------------------------ */
const STEPS = [
  {
    n: 1,
    icon: UserPlus,
    title: "Como aderir",
    short: "Primeiro orçamento regularizado",
    body: [
      "Para participar do Faz Depois Paga, a escola deverá, inicialmente, contratar um serviço convencional com a AMC Escolas.",
      "Esse primeiro serviço é executado normalmente, com emissão da nota fiscal e pagamento pela escola.",
      "Depois da conclusão, da nota emitida e do pagamento regularizado, a escola é cadastrada e ativada no sistema Faz Depois Paga.",
      "A ativação está condicionada à conclusão e regularização integral desse primeiro serviço.",
    ],
  },
  {
    n: 2,
    icon: PlayCircle,
    title: "Como funciona após a ativação",
    short: "Solicitações rápidas dentro dos limites",
    body: [
      "Depois de habilitada, a escola pode solicitar pequenos serviços de manutenção à AMC Escolas, conforme as condições do cadastro.",
      "Os serviços podem ser solicitados ao longo do período de utilização, respeitando dois limites previamente definidos:",
    ],
    bullets: [
      { icon: Clock, label: "Limite de prazo", desc: "Período máximo durante o qual a escola pode utilizar o sistema." },
      { icon: Wallet, label: "Limite de valor", desc: "Valor máximo acumulado permitido para os serviços realizados no ciclo." },
    ],
    tail:
      "Tudo é registrado no sistema, permitindo acompanhamento em tempo real dos atendimentos e valores acumulados.",
  },
  {
    n: 3,
    icon: CheckCircle2,
    title: "Quando o ciclo é encerrado",
    short: "Prazo OU valor — basta um deles",
    body: [
      "O ciclo do Faz Depois Paga é encerrado quando qualquer um dos dois limites for atingido: prazo ou valor.",
      "Ao atingir uma dessas condições, é realizado o fechamento do ciclo, com emissão da nota fiscal correspondente aos serviços acumulados e a regularização do pagamento.",
      "Prazo e valor funcionam de forma independente: basta atingir um deles para encerrar o ciclo.",
    ],
  },
  {
    n: 4,
    icon: RefreshCw,
    title: "Renovação",
    short: "Novo ciclo, novos limites",
    body: [
      "Depois do fechamento do ciclo, da nota emitida e do pagamento regularizado, o sistema pode ser reiniciado para um novo período de utilização.",
      "Com a renovação, a escola volta a contar com os limites de prazo e valor definidos para o novo ciclo.",
      "A continuidade depende sempre da regularização do ciclo anterior.",
    ],
  },
  {
    n: 5,
    icon: ShieldCheck,
    title: "Compromisso com organização e transparência",
    short: "Controle total, do início ao fim",
    body: [
      "O Faz Depois Paga foi desenvolvido para simplificar a rotina das escolas e facilitar o atendimento de pequenas demandas de manutenção.",
      "Mantém controle dos serviços executados, dos valores acumulados, dos prazos e dos fechamentos fiscais.",
    ],
  },
];

const HowItWorks = ({ mockups }) => (
  <Section id="como-funciona" className="bg-gradient-to-b from-slate-50 to-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>Passo a passo</Kicker>
      <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-black text-[#0b1536]">
        Como funciona o Faz Depois Paga
      </h2>
      <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">
        Um fluxo simples, pensado para a rotina da direção da escola.
      </p>
    </div>

    <div className="mt-12 relative">
      {/* Timeline vertical line */}
      <div className="hidden md:block absolute left-6 top-6 bottom-10 w-0.5 bg-gradient-to-b from-emerald-500 via-emerald-400 to-emerald-100" />

      <div className="space-y-6 md:space-y-10">
        {STEPS.map((s, i) => (
          <div key={s.n} className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="relative rounded-3xl bg-white border border-slate-200 p-6 sm:p-7 card-shadow md:ml-16">
              {/* Number badge */}
              <div className="absolute -left-7 top-6 hidden md:flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-black text-lg shadow-lg shadow-emerald-500/40">
                {s.n}
              </div>
              <div className="flex items-center gap-3 md:hidden">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-black text-base flex items-center justify-center">
                  {s.n}
                </div>
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 mb-1">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                  Passo {s.n}
                </span>
              </div>
              <h3 className="mt-2 md:mt-0 font-heading text-xl sm:text-2xl font-black text-[#0b1536]">
                {s.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold text-emerald-700 uppercase tracking-wider">
                {s.short}
              </p>
              <div className="mt-4 space-y-2.5 text-[13.5px] text-slate-600 leading-relaxed">
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                  {s.bullets.map((b) => (
                    <li
                      key={b.label}
                      className="flex gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3"
                    >
                      <div className="w-8 h-8 shrink-0 rounded-lg bg-white border border-emerald-200 flex items-center justify-center">
                        <b.icon className="w-4 h-4 text-emerald-600" strokeWidth={2.4} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[12px] font-black text-emerald-900">{b.label}</p>
                        <p className="text-[11.5px] text-emerald-800 leading-snug">
                          {b.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {s.tail && (
                <p className="mt-4 text-[13.5px] text-slate-600 leading-relaxed">
                  {s.tail}
                </p>
              )}
            </div>

            {/* Small mockup to the right (desktop) */}
            {mockups[i % mockups.length] && (
              <div className="hidden lg:block">
                <PhoneCard
                  src={mockups[i % mockups.length]}
                  rotation={i % 2 === 0 ? -4 : 4}
                  delay={i * 0.4}
                  scale={0.8}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    <div className="mt-12 flex justify-center">
      <QueroContratarCTA testid="howitworks-cta" />
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */
const Stats = () => (
  <Section className="bg-[#061021] text-white">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] bg-[length:24px_24px] opacity-50" />
    <div className="relative grid sm:grid-cols-3 gap-4">
      {[
        { icon: Zap, title: "Serviço imediato", desc: "A manutenção começa no mesmo dia da solicitação." },
        { icon: ShieldCheck, title: "Zero risco pra escola", desc: "Você só paga depois do serviço concluído e aprovado." },
        { icon: TrendingUp, title: "Rotina sob controle", desc: "Fotos, nota fiscal e termos — tudo registrado no portal." },
      ].map((it) => (
        <div
          key={it.title}
          className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-3">
            <it.icon className="w-5 h-5 text-emerald-300" strokeWidth={2.4} />
          </div>
          <p className="font-heading text-lg font-black">{it.title}</p>
          <p className="mt-1 text-[13px] text-white/70 leading-relaxed">{it.desc}</p>
        </div>
      ))}
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Big CTA band                                                        */
/* ------------------------------------------------------------------ */
const BigCTA = () => (
  <Section className="bg-gradient-to-br from-emerald-500 via-emerald-500 to-teal-600 text-white overflow-hidden">
    <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
    <div className="absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
    <div className="relative text-center max-w-3xl mx-auto">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 border border-white/30 text-[10px] font-black uppercase tracking-widest">
        <Rocket className="w-3 h-3" /> Comece hoje
      </div>
      <h2 className="mt-4 font-heading text-3xl sm:text-5xl font-black leading-[1.05]">
        Garanta a sua escola <br className="hidden sm:block" /> no sistema Faz Depois Paga.
      </h2>
      <p className="mt-4 text-white/90 text-[14px] sm:text-base max-w-xl mx-auto leading-relaxed">
        Chame a AMC no WhatsApp, feche o seu <strong>primeiro orçamento</strong> e,
        depois dele regularizado, a sua escola já entra na modalidade FDP.
      </p>
      <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
        <QueroContratarCTA testid="bigcta-main" />
        <a
          href={whatsappLink("Olá AMC Escolas! Tenho dúvidas sobre o sistema Faz Depois Paga.")}
          target="_blank"
          rel="noreferrer"
          data-testid="bigcta-doubts"
          className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-2xl bg-white hover:bg-slate-50 text-emerald-700 border-2 border-white text-sm font-black shadow-xl shadow-black/10"
        >
          <MessageCircle className="w-5 h-5" /> Tirar dúvidas no WhatsApp
        </a>
      </div>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */
const FAQ_ITEMS = [
  {
    q: "Toda escola pode entrar no FDP?",
    a: "A adesão depende da conclusão e regularização integral do primeiro serviço convencional contratado com a AMC. Depois disso, a escola é cadastrada e ativada na modalidade.",
  },
  {
    q: "O que acontece se o prazo acabar antes do valor?",
    a: "O ciclo é encerrado assim que qualquer um dos dois limites (prazo ou valor) for atingido. É emitida a nota fiscal referente aos serviços acumulados e, depois do pagamento, um novo ciclo pode ser aberto.",
  },
  {
    q: "Tem taxa extra pra usar o Faz Depois Paga?",
    a: "Não. O FDP é uma modalidade de contratação, não um serviço avulso. Você paga apenas pelos serviços executados dentro do ciclo.",
  },
  {
    q: "Como a escola acompanha os serviços?",
    a: "Pelo portal AMC Escolas (web e aplicativo), com fotos de antes e depois, orçamentos, notas fiscais, termos de colaboração e PDDE, tudo numa só tela.",
  },
];

const FAQ = () => (
  <Section className="bg-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>Dúvidas comuns</Kicker>
      <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-black text-[#0b1536]">
        Perguntas que a direção faz
      </h2>
    </div>
    <div className="mt-10 grid md:grid-cols-2 gap-4">
      {FAQ_ITEMS.map((it, i) => (
        <details
          key={i}
          data-testid={`fdp-faq-${i}`}
          className="group rounded-2xl border border-slate-200 bg-slate-50 open:bg-white open:border-emerald-300 open:shadow-lg open:shadow-emerald-500/5 transition-all p-5"
        >
          <summary className="flex items-start gap-3 cursor-pointer list-none">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 group-open:bg-emerald-600 group-open:text-white transition-colors">
              <BadgeCheck className="w-4 h-4" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-[15px] font-black text-[#0b1536] leading-tight">
                {it.q}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform mt-2" />
          </summary>
          <p className="mt-3 ml-12 text-[13.5px] text-slate-600 leading-relaxed">
            {it.a}
          </p>
        </details>
      ))}
    </div>
    <div className="mt-10 flex justify-center">
      <QueroContratarCTA testid="faq-cta" variant="outline" />
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
const Footer = () => (
  <footer className="bg-[#0b1536] text-white/80 px-5 sm:px-8 py-10">
    <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-start gap-6">
      <div>
        <Logo withIcon />
        <p className="mt-3 text-[12px] text-white/60 max-w-sm leading-relaxed">
          AMC Manutenção e Serviços — mais praticidade para a manutenção da sua
          escola, com organização, transparência e confiança.
        </p>
      </div>
      <div className="flex flex-col gap-2 text-[13px]">
        <Link to="/" className="hover:text-white transition-colors" data-testid="fdp-footer-home">
          ← Página inicial
        </Link>
        <a
          href={whatsappLink("Olá AMC Escolas!")}
          target="_blank"
          rel="noreferrer"
          className="hover:text-white transition-colors inline-flex items-center gap-1.5"
        >
          <Phone className="w-3.5 h-3.5" /> WhatsApp (11) 95664-9968
        </a>
      </div>
    </div>
  </footer>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function FazDepoisPagaPublic() {
  const mockups = useMockups();
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="fdp-public-page">
      <Hero mockups={mockups} />
      <BeforeAfter />
      <HowItWorks mockups={mockups} />
      <Stats />
      <FAQ />
      <BigCTA />
      <Footer />

      {/* Floating sticky CTA for mobile */}
      <div className="fixed bottom-4 left-3 right-3 z-40 sm:hidden">
        <QueroContratarCTA testid="sticky-cta" size="md" />
      </div>
    </div>
  );
}
